<template>
  <div class="wrapper">
    <div v-if="isFocused" :class="{ title: !isFilled, title_focus: isFocused }">
      {{ props.placeholder }}
    </div>
    <div
      v-else-if="!isFocused && isFilled && !errorMessage && !props.disabled"
      class="title"
    >
      {{ props.placeholder }}
    </div>

    <div
      v-else-if="!isFocused && isFilled && !errorMessage && props.disabled"
      class="titleDisabled"
    >
      {{ props.placeholder }}
    </div>
    <div v-else-if="errorMessage" class="titleError">
      {{ props.placeholder }}
    </div>
    <Field
      v-bind="$attrs"
      id="input"
      v-model="inputValue"
      :type="type"
      class="input"
      :class="{
        'placeholder-text': !inputValue,
        focused: isFocused,
        inputError: errorMessage,
      }"
      :name="field"
      :disabled="disabled"
      :placeholder="placeholderHidden"
      @input="updateValue"
      @keypress="handleKeyPress"
      @keydown="handleKeyPress"
      :maxlength="maxLength"
      :minlength="minLength"
      v-on="validationListeners"
      @focus="handleInputFocus"
      :number="number"
      :pattern="pattern"
      @focusout="handleBlurOut"
      @mousedown.stop="handleClick"
      :autocomplete="autocomplete"
      :clear="clear"
      @paste="handlePaste"
      :postCode="postCode"
      :noCapital="noCapital"
    />
    <!-- @mouseleave="handleBlurOut" -->
    <!-- <pre class="help" v-if="!errorMessage"> {{ errorMessage }}</pre> -->
    <pre class="help"> {{ errorMessage }}</pre>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted } from "vue";
import { useField, Field } from "vee-validate";
import i18n from "@/plugin/i18n";

const { t } = i18n.global;

const props = defineProps({
  id: {
    type: String,
    // required: true,
  },
  autocomplete: {
    type: String,
  },
  validateRule: {
    type: String,
  },
  modelValue: {
    type: [Number, String],
  },
  type: {
    type: String,
    default: "text",
  },
  field: {
    type: String,
    required: true,
  },
  placeholder: {
    type: String,
    default: "",
  },
  customPlaceholder: {
    type: String,
    default: "",
  },
  minLength: {
    type: Number,
  },
  maxLength: {
    type: Number,
    default: 50,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  required: {
    type: Boolean,
    default: true,
  },
  validationType: {
    type: String,
    default: "",
  },
  number: {
    type: String,
  },
  pattern: {
    type: [String, RegExp],
  },
  class: {
    type: String,
  },
  clear: {
    type: String,
  },
  postCode: {
    type: Boolean,
  },
  trim: {
    type: Boolean,
    default: false,
  },
  trimZero: {
    type: Boolean,
    default: false,
  },
  noCapital: {
    type: Boolean,
    default: false,
  },
  idType: {
    type: String,
  },
});

const isFocused = ref(false);
const isFilled = ref(false);
const inputValue = ref(props.modelValue !== "" ? props.modelValue : "");
const detectClick = ref(false);

if (props.modelValue) {
  isFilled.value = true;
}

const { value, errorMessage, handleBlur } = useField(
  () => props.field,
  props.validateRule,
  {
    validateOnValueUpdate: false,
  }
);
const disableAutoFocus = ref(false);
const handleInputFocus = () => {
  disableAutoFocus.value = false;
  isFocused.value = true;
  isFilled.value = false;
};

const emits = defineEmits(["update:modelValue"]);
const updateValue = (event: InputEvent) => {
  const inputText = (event.target as HTMLInputElement).value;
  const target = event.target as HTMLInputElement;
  inputValue.value = target.value;
  const isNumericInput = /^\d+$/.test(inputText);
  const inputElement = event.target as HTMLInputElement;
  const start = inputElement.selectionStart;

  if (props.number === "true" && !isNumericInput) {
    event.preventDefault();
  }
  if (props.postCode == true) {
    const target = event.target as HTMLInputElement | null;
    emits("update:modelValue", inputValue.value);

    inputValue.value = props.modelValue;
  } else {
    if (props.noCapital == false) {
      inputValue.value =
        typeof inputValue.value === "string"
          ? inputValue.value.toUpperCase()
          : inputValue.value;
      nextTick(() => {
        if (inputElement.setSelectionRange) {
          inputElement.setSelectionRange(start, start);
        }
      });
      emits("update:modelValue", inputValue.value);
      inputValue.value = props.modelValue;
    } else {
      emits("update:modelValue", inputValue.value);
      inputValue.value = props.modelValue;
    }
  }
};

const validationListeners = {
  blur: (evt: Event | undefined) => handleBlur(evt, true),
};

const handleClick = () => {
  setTimeout(() => {
    isFocused.value = true;
    isFilled.value = true;
  }, 0);
};

const test = ref("");

const handleKeyPress = (event: KeyboardEvent) => {
  const isNumericKey = /^\d+$/.test(event.key);
  const allowedKeys = [
    "0",
    "1",
    "2",
    "3",
    "4",
    "5",
    "6",
    "7",
    "8",
    "9",
    "Backspace",
    "ArrowLeft",
    "ArrowRight",
    "Delete",
    "Tab",
  ];

  if (props.number == "true" && !allowedKeys.includes(event.key)) {
    event.preventDefault();
  }
};

const handleBlurOut = () => {
  if (inputValue.value == "") {
    isFilled.value = false;
    isFocused.value = false;
  } else {
    isFilled.value = true;
    isFocused.value = false;
  }
  if (
    props.trim &&
    inputValue.value?.toString().startsWith("0") &&
    inputValue.value.toString().length > 1
  ) {
    inputValue.value = inputValue.value?.toString().substring(1);
    emits("update:modelValue", inputValue.value);
  }
  if (
    props.trimZero &&
    inputValue.value?.toString().startsWith("0") &&
    inputValue.value.toString().length >= 1
  ) {
    inputValue.value = inputValue.value?.toString().substring(1);
    emits("update:modelValue", inputValue.value);
  }
};

const placeholderHidden = computed(() => {
  if (detectClick.value && props.customPlaceholder != "") {
    return detectClick.value ? props.customPlaceholder : props.placeholder;
  } else {
    return isFocused.value ? "" : props.placeholder;
  }
});

const handlePaste = async (event: ClipboardEvent) => {
  if (props.number == "true") {
    try {
      let pastedText = "";

      if (navigator.clipboard) {
        pastedText = await navigator.clipboard.readText();
      } else {
        pastedText = event.clipboardData?.getData("text") || "";
      }

      if (!/^\d+$/.test(pastedText)) {
        event.preventDefault();
      }
    } catch (error) {
      console.error("Error reading clipboard:", error);
    }
  }
};

watch(inputValue, (newValue) => {
  value.value = newValue;
});

watch(isFocused, (newValue) => {
  if (!detectClick.value) {
    detectClick.value = true;
  }
});

watch(
  () => props.modelValue,
  (newModelValue) => {
    if (newModelValue != "") {
      value.value = newModelValue;
      isFilled.value = true;
    } else {
      isFilled.value = false;
      value.value = newModelValue;
    }
  }
);

watch(
  () => props.idType,
  (newValue, oldValue) => {
    if (newValue) {
      if (
        ((oldValue === "0" || oldValue === "2" || oldValue === "3") &&
          newValue === "1") ||
        oldValue === "1"
      ) {
        inputValue.value = "";
        emits("update:modelValue", "");
      }
    }
  }
);

// watch(
//   () => props.clear,
//   (newValue) => {
//     if (newValue) {
//       if (props.field == "idNo") {
//         inputValue.value = "";
//         emits("update:modelValue", "");
//       }
//     }
//   }
// );
</script>

<style scoped>
.wrapper {
  position: relative;
}

.help {
  color: red;
  margin-bottom: 0px;
  margin-top: 8px;
  font: 400 16px/130% "Outfit", sans-serif;
  word-wrap: break-word;
  white-space: pre-wrap;
}

.input {
  border: 2px solid #dbdfea;
  padding: 14px 31px 14px 22px;
  border-radius: 10px;
  width: 100%;
  color: rgba(0, 0, 0, 0.87);
  font: 400 16px/130% "Outfit", sans-serif;
  outline-color: #ac2887;
}

.inputError {
  border: 2px solid red;
  padding: 14px 31px 14px 22px;
  border-radius: 10px;
  width: 100%;
  color: rgba(0, 0, 0, 0.87);
  font: 400 16px/130% "Outfit", sans-serif;
  outline-color: #ac2887;
}

.input.focused {
  border-color: #ac2887;
  outline: none;
}

.input:focus {
  box-shadow: none;
  outline: none;
}

.input::placeholder {
  color: var(--black-low-38, rgba(0, 0, 0, 0.38));
  font: var(--subtitle-1-medium, 500 16px/130% "Outfit", sans-serif);
}

input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px white inset !important;
}

input:not(:placeholder-shown) {
  background-color: #ffffff !important;
}

input:placeholder-shown {
  background-color: #ffffff !important;
}

.title {
  position: absolute;
  top: -8px;
  left: 15px;
  color: rgba(0, 0, 0, 0.38);
  font: 500 12px/130% "Outfit", sans-serif;
  background: white;
  padding: 0 5px;
}

.titleDisabled {
  position: absolute;
  top: -8px;
  left: 15px;
  color: rgba(0, 0, 0, 0.38);
  font: 500 12px/130% "Outfit", sans-serif;
  background: linear-gradient(to bottom, white 52%, #dbdfea 48%);
  padding: 0 5px;
}

.titleError {
  position: absolute;
  top: -8px;
  left: 15px;
  color: red;
  font: 500 12px/130% "Outfit", sans-serif;
  background-color: white;
  padding: 0 5px;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(100%);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
@keyframes slideOut {
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(100%);
  }
}

.title_focus {
  position: absolute;
  top: -8px;
  left: 15px;
  color: #ac2887;
  font: 500 12px/130% "Outfit", sans-serif;
  background-color: white;
  padding: 0 5px;
  opacity: 0;
  transform: translateY(100%);

  /* Animation */
  animation: slideIn 0.3s ease-out forwards;
}

.title_remove_focus {
  /* Animation */
  animation: slideOut 0.3s ease-out forwards;
}

.input[disabled] {
  background-color: #dbdfea !important;
}

input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
  appearance: none;
}
</style>
